import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';

/**
 * 数据分析。
 *
 * ★ 这一屏是**先出了一份体检报告、看清哪些维度真有信息量之后**才做的,
 *   不是把能算的都摆上去。四个页签的顺序就是它们的价值排序:
 *
 *   综合 → 大区 → 司机 → 地址质量
 *
 *   而「地址质量」里那张**判不出区的地址按郊区归类**,是整屏最该被看到的一张:
 *   它把配置缺口直接顶出来。当初正是它一眼指出 East Tāmaki 一带
 *   八十个地址全军覆没 —— 而「按大区看单量」的图表永远发现不了这件事,
 *   因为那个区在图上根本不存在。
 *
 * ★ 时间范围默认最近 30 天,不是「今天」。
 *   今天的数字在「概览」那一屏已经有了;来这一屏的人要看的是趋势,
 *   而一天的数据看不出趋势。
 */

type Tab = 'summary' | 'zones' | 'drivers' | 'address';

interface Summary {
  range: { from: string; to: string; timezone: string };
  totals: {
    created: number;
    delivered: number;
    cancelled: number;
    returned: number;
    needsReview: number;
    hadFailure: number;
    codOrders: number;
    revenue: number;
    payout: number;
  };
  deliveryRate: number | null;
  timing: {
    samples: number;
    avgMinutes: number | null;
    medianMinutes: number | null;
    p90Minutes: number | null;
  };
  daily: Array<{ date: string; created: number; delivered: number; cancelled: number }>;
  hourly: Array<{ hour: number; created: number }>;
}

interface ZoneRow {
  code: string;
  name: string;
  isRemote: boolean;
  orders: number;
  delivered: number;
  cancelled: number;
  needsReview: number;
  avgPrice: string;
  avgPayout: string;
}

interface DriverRow {
  driverNo: string;
  name: string;
  status: string;
  taken: number;
  delivered: number;
  cancelled: number;
  hadFailure: number;
  payout: string;
  avgDeliverMinutes: string | null;
}

interface AddressQuality {
  geocode: Array<{ status: string; count: number }>;
  matching: Array<{ matchType: string; count: number; avgConfidence: string | null }>;
  unmatched: {
    total: number;
    capped: boolean;
    bySuburb: Array<{ suburb: string; count: number }>;
  };
}

/** 分转元。金额一律在展示这一处换算,不在别处提前除 */
const money = (cents: number | string) =>
  (Number(cents) / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2 });

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

const GEOCODE_LABELS: Record<string, string> = {
  OK: '已定位',
  PENDING: '待定位',
  MANUAL: '手工填的坐标',
  FAILED: '定位失败',
};

const MATCH_LABELS: Record<string, string> = {
  KEYWORD: '按关键词判出',
  MANUAL: '人工指定',
  POSTCODE: '按邮编判出',
  UNMATCHED: '判不出区',
};

export function Analytics() {
  const [tab, setTab] = useState<Tab>('summary');
  const [from, setFrom] = useState(() => isoDay(new Date(Date.now() - 29 * 24 * 3600_000)));
  const [to, setTo] = useState(() => isoDay(new Date()));

  const [summary, setSummary] = useState<Summary | null>(null);
  const [zones, setZones] = useState<ZoneRow[] | null>(null);
  const [drivers, setDrivers] = useState<DriverRow[] | null>(null);
  const [address, setAddress] = useState<AddressQuality | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * ★ 只拉**当前页签**要的那一份。
   *   四份聚合都是全表扫,一次全拉等于三份白算 ——
   *   而人打开这一屏时只在看其中一块。
   */
  const load = useCallback(async () => {
    const qs = `?from=${from}&to=${to}`;
    setBusy(true);
    setError(null);
    try {
      if (tab === 'summary') setSummary(await api<Summary>(`/analytics/summary${qs}`));
      if (tab === 'zones') setZones(await api<ZoneRow[]>(`/analytics/zones${qs}`));
      if (tab === 'drivers') setDrivers(await api<DriverRow[]>(`/analytics/drivers${qs}`));
      if (tab === 'address') setAddress(await api<AddressQuality>(`/analytics/address${qs}`));
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '读不到分析数据');
    } finally {
      setBusy(false);
    }
  }, [tab, from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="p-4 max-w-6xl lg:p-6">
      <div className="mb-1 flex items-baseline gap-3 flex-wrap">
        <h1 className="text-xl font-semibold lg:text-2xl text-slate-900 dark:text-slate-100">
          数据分析
        </h1>
        {summary && (
          <span className="text-xs text-slate-400">{summary.range.timezone} · 按建单时间统计</span>
        )}
      </div>

      {/* 时间范围。放在页签**上面** —— 它对四个页签都生效 */}
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-sm text-slate-600 dark:text-slate-400">
          <span className="block mb-1">从</span>
          <input
            type="date"
            aria-label="开始日期"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white
                       dark:bg-slate-800 px-2 py-1 text-sm"
          />
        </label>
        <label className="text-sm text-slate-600 dark:text-slate-400">
          <span className="block mb-1">到</span>
          <input
            type="date"
            aria-label="结束日期"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white
                       dark:bg-slate-800 px-2 py-1 text-sm"
          />
        </label>
        <div className="flex gap-2">
          {(
            [
              ['近 7 天', 6],
              ['近 30 天', 29],
              ['近 90 天', 89],
            ] as const
          ).map(([label, days]) => (
            <button
              key={label}
              onClick={() => {
                setFrom(isoDay(new Date(Date.now() - days * 24 * 3600_000)));
                setTo(isoDay(new Date()));
              }}
              className="rounded-lg border border-slate-300 dark:border-slate-600 px-2.5 py-1
                         text-sm text-slate-600 dark:text-slate-300
                         hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              {label}
            </button>
          ))}
        </div>
        {busy && <span className="text-sm text-slate-400">读取中…</span>}
      </div>

      <nav aria-label="分析维度" className="mt-5 flex gap-1 border-b border-slate-200 dark:border-slate-700">
        {(
          [
            ['summary', '综合'],
            ['zones', '大区'],
            ['drivers', '司机'],
            ['address', '地址质量'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            aria-current={tab === key ? 'page' : undefined}
            className={`px-3 py-2 text-sm -mb-px border-b-2 transition ${
              tab === key
                ? 'border-brand-600 text-brand-700 dark:text-brand-400 font-medium'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 dark:border-red-900 bg-red-50
                        dark:bg-red-950 px-4 py-3 text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {tab === 'summary' && summary && <SummaryTab data={summary} />}
      {tab === 'zones' && zones && <ZonesTab rows={zones} />}
      {tab === 'drivers' && drivers && <DriversTab rows={drivers} />}
      {tab === 'address' && address && <AddressTab data={address} />}
    </div>
  );
}

function SummaryTab({ data }: { data: Summary }) {
  const daily = fillGaps(data.daily);
  const maxDaily = Math.max(1, ...daily.map((d) => d.created));
  const maxHour = Math.max(1, ...data.hourly.map((h) => h.created));
  const t = data.totals;

  return (
    <div className="mt-5 space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Metric label="建单" value={t.created.toLocaleString()} />
        <Metric label="送达" value={t.delivered.toLocaleString()} tone="good" />
        <Metric
          label="已取消"
          value={t.cancelled.toLocaleString()}
          tone={t.cancelled > 0 ? 'bad' : undefined}
          note="主动取消或作废"
        />
        <Metric
          label="送达率"
          value={data.deliveryRate == null ? '—' : `${data.deliveryRate}%`}
          note="分母是已经有结果的单"
        />
        <Metric
          label="待复核"
          value={t.needsReview.toLocaleString()}
          tone={t.needsReview > 0 ? 'bad' : undefined}
          note="判不出区或地址存疑"
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric label="运费收入" value={money(t.revenue)} note="只算已送达的单" />
        <Metric label="司机酬劳" value={money(t.payout)} note="只算已送达的单" />
        <Metric label="代收货款单" value={t.codOrders.toLocaleString()} />
        <Metric label="投递失败过" value={t.hadFailure.toLocaleString()} />
      </div>

      <Panel
        title="送达时长"
        hint={
          /*
           * ★ 中位数摆在第一位。一单跨城的异常件能把平均值拉高一倍,
           *   而调度关心的是「一般多久」。
           */
          `样本 ${data.timing.samples.toLocaleString()} 单 —— 只统计送达时间不早于建单时间的单`
        }
      >
        {data.timing.samples === 0 ? (
          <Empty>这段时间还没有送达的单</Empty>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <Metric label="中位数" value={fmtMinutes(data.timing.medianMinutes)} note="一般多久" />
            <Metric label="平均" value={fmtMinutes(data.timing.avgMinutes)} />
            <Metric label="九成单在" value={fmtMinutes(data.timing.p90Minutes)} note="P90" />
          </div>
        )}
      </Panel>

      <Panel title="每天的单量" hint="灰=进行中/其他, 绿=已送达, 红=已取消">
        {daily.length === 0 ? (
          <Empty>这段时间没有单</Empty>
        ) : (
          <div className="overflow-x-auto">
            <div className="flex items-end gap-1 min-w-full" style={{ height: 130 }}>
              {daily.map((d) => (
                <div
                  key={d.date}
                  className="flex-1 min-w-[10px] flex flex-col justify-end"
                  title={`${d.date} 建单 ${d.created} · 送达 ${d.delivered} · 取消 ${d.cancelled}`}
                >
                  {/*
                    ★ 一根柱子的总高就是建单数，内部按 送达(绿) / 取消(红) / 进行中(灰) 分段展示。
                  */}
                  <div
                    className="w-full bg-slate-300 dark:bg-slate-600 rounded-t flex flex-col justify-end overflow-hidden"
                    style={{ height: `${(d.created / maxDaily) * 110}px` }}
                  >
                    {d.created > 0 && d.cancelled > 0 && (
                      <div
                        className="w-full bg-rose-400 dark:bg-rose-500"
                        style={{
                          height: `${(d.cancelled / d.created) * 100}%`,
                        }}
                      />
                    )}
                    {d.created > 0 && d.delivered > 0 && (
                      <div
                        className="w-full bg-green-500 dark:bg-green-600"
                        style={{
                          height: `${(d.delivered / d.created) * 100}%`,
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-1 flex justify-between text-xs text-slate-400">
              <span>{daily[0]?.date}</span>
              <span>{daily[daily.length - 1]?.date}</span>
            </div>
          </div>
        )}
      </Panel>

      <Panel title="一天里什么时候进单" hint="按建单时刻,租户时区">
        <div className="flex items-end gap-1" style={{ height: 110 }}>
          {Array.from({ length: 24 }, (_, h) => {
            const row = data.hourly.find((x) => x.hour === h);
            const n = row?.created ?? 0;
            return (
              <div
                key={h}
                className="flex-1 flex flex-col justify-end"
                title={`${h}:00 — ${n} 单`}
              >
                <div
                  className="bg-brand-500 rounded-t"
                  style={{ height: `${(n / maxHour) * 90}px` }}
                />
                {h % 6 === 0 && <div className="mt-1 text-[10px] text-slate-400 text-center">{h}</div>}
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}

function ZonesTab({ rows }: { rows: ZoneRow[] }) {
  if (rows.length === 0) return <Empty className="mt-5">这段时间没有判进任何大区的单</Empty>;
  return (
    <Panel title="按大区" hint={`${rows.length} 个区有单 · 按单量排`} className="mt-5">
      <Table
        head={['大区', '单量', '送达', '已取消', '待复核', '均运费', '均酬劳']}
        rows={rows.map((z) => [
          <span key="n">
            {z.name}
            <span className="ml-1.5 text-xs text-slate-400">{z.code}</span>
            {z.isRemote && (
              <span className="ml-1.5 rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5
                               text-[11px] text-amber-700 dark:text-amber-300">偏远</span>
            )}
          </span>,
          z.orders.toLocaleString(),
          z.delivered.toLocaleString(),
          z.cancelled > 0 ? (
            <span key="c" className="text-rose-600 dark:text-rose-400">
              {z.cancelled.toLocaleString()}
            </span>
          ) : (
            '0'
          ),
          z.needsReview > 0 ? (
            <span key="r" className="text-amber-600 dark:text-amber-400">{z.needsReview}</span>
          ) : (
            '0'
          ),
          money(z.avgPrice),
          money(z.avgPayout),
        ])}
      />
    </Panel>
  );
}

function DriversTab({ rows }: { rows: DriverRow[] }) {
  if (rows.length === 0) return <Empty className="mt-5">这段时间没有司机接过单</Empty>;
  return (
    <Panel title="按司机" hint={`${rows.length} 人接过单 · 按送达数排`} className="mt-5">
      <Table
        head={['司机', '接单', '送达', '已取消', '失败过', '取件到送达', '酬劳']}
        rows={rows.map((d) => [
          <span key="n">
            {d.name}
            <span className="ml-1.5 text-xs text-slate-400">{d.driverNo}</span>
            {d.status !== 'ACTIVE' && (
              <span className="ml-1.5 rounded bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5
                               text-[11px] text-slate-600 dark:text-slate-300">{d.status}</span>
            )}
          </span>,
          d.taken.toLocaleString(),
          d.delivered.toLocaleString(),
          d.cancelled > 0 ? (
            <span key="c" className="text-rose-600 dark:text-rose-400">
              {d.cancelled.toLocaleString()}
            </span>
          ) : (
            '0'
          ),
          d.hadFailure > 0 ? (
            <span key="f" className="text-amber-600 dark:text-amber-400">{d.hadFailure}</span>
          ) : (
            '0'
          ),
          fmtMinutes(d.avgDeliverMinutes == null ? null : Number(d.avgDeliverMinutes)),
          money(d.payout),
        ])}
      />
    </Panel>
  );
}

function AddressTab({ data }: { data: AddressQuality }) {
  const totalMatch = data.matching.reduce((s, m) => s + m.count, 0);
  return (
    <div className="mt-5 space-y-6">
      <Panel title="判区结果" hint="判不出区的单会进待复核,而且算不出分区价、匹配不到默认司机">
        <Table
          head={['怎么判出来的', '单量', '占比', '平均强度']}
          rows={data.matching.map((m) => [
            MATCH_LABELS[m.matchType] ?? m.matchType,
            m.count.toLocaleString(),
            totalMatch > 0 ? `${Math.round((m.count / totalMatch) * 100)}%` : '—',
            m.avgConfidence ?? '—',
          ])}
        />
      </Panel>

      <Panel title="地理编码" hint="没有坐标的单上不了地图,也算不出按距离的费用">
        <Table
          head={['状态', '单量']}
          rows={data.geocode.map((g) => [
            GEOCODE_LABELS[g.status] ?? g.status,
            g.count.toLocaleString(),
          ])}
        />
      </Panel>

      {/*
        ★ 整屏最该被看到的一张。
          按大区看单量的图表发现不了配置缺口 —— 缺的那个区在图上根本不存在。
          按「地址里写的郊区」归类,缺口才会自己浮上来。
      */}
      <Panel
        title="判不出区的地址,按地址里写的郊区归类"
        hint={
          data.unmatched.capped
            ? '★ 判不出区的单太多,这里只扫了前 5000 条 —— 排名可参考,绝对数不完整'
            : '排在前面的通常不是「地址写错了」,而是这个郊区还没配进任何大区'
        }
      >
        {data.unmatched.bySuburb.length === 0 ? (
          <Empty>这段时间的单都判出区了</Empty>
        ) : (
          <>
            <Table
              head={['地址里写的郊区', '单量']}
              rows={data.unmatched.bySuburb.map((b) => [b.suburb, b.count.toLocaleString()])}
            />
            <p className="mt-3 text-xs text-slate-400">
              「郊区」是从地址里按逗号切出来的,不是权威字段 —— 地址写得不规范时这里会是整条地址。
              共 {data.unmatched.total.toLocaleString()} 条判不出区,只列前 30 个。
            </p>
          </>
        )}
      </Panel>
    </div>
  );
}

/* ── 小组件 ─────────────────────────────────────────────── */

/**
 * 把中间缺掉的日期补成 0。
 *
 * ★ 后端只返回**有单的那些天**。直接画的话,没单的那天会从横轴上
 *   消失而不是变成 0 —— 相邻两根柱子看起来是连着的两天,
 *   实际隔了一周。趋势图最不该骗人的就是横轴。
 *
 * ★ 只补首尾之间的空档,不往两头延伸:选了「近 90 天」而只有一周数据时,
 *   前面 83 根 0 高度的柱子除了把有数据的那一周压扁之外没有任何作用。
 */
function fillGaps(rows: Summary['daily']): Summary['daily'] {
  if (rows.length < 2) return rows;
  const out: Summary['daily'] = [];
  const day = (s: string) => new Date(`${s}T00:00:00Z`).getTime();
  let cursor = day(rows[0]!.date);
  const end = day(rows[rows.length - 1]!.date);
  const known = new Map(rows.map((r) => [r.date, r]));
  // 上限兜底:范围再大也不至于画出上万根柱子
  for (let i = 0; cursor <= end && i < 400; i++) {
    const key = new Date(cursor).toISOString().slice(0, 10);
    out.push(known.get(key) ?? { date: key, created: 0, delivered: 0, cancelled: 0 });
    cursor += 24 * 3600_000;
  }
  return out;
}

function fmtMinutes(m: number | null): string {
  if (m == null) return '—';
  if (m < 60) return `${Math.round(m)} 分钟`;
  const h = Math.floor(m / 60);
  const rest = Math.round(m % 60);
  return rest === 0 ? `${h} 小时` : `${h} 小时 ${rest} 分`;
}

function Metric({
  label,
  value,
  tone,
  note,
}: {
  label: string;
  value: number | string;
  tone?: 'good' | 'bad';
  note?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white
                    dark:bg-slate-800 px-4 py-3">
      <div className="text-sm text-slate-500">{label}</div>
      <div
        className={`mt-0.5 text-2xl font-bold tabular-nums ${
          tone === 'good'
            ? 'text-green-600 dark:text-green-400'
            : tone === 'bad'
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-slate-900 dark:text-slate-100'
        }`}
      >
        {value}
      </div>
      {note && <div className="mt-0.5 text-xs text-slate-400">{note}</div>}
    </div>
  );
}

function Panel({
  title,
  hint,
  className,
  children,
}: {
  title: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`rounded-xl border border-slate-200 dark:border-slate-700 bg-white
                  dark:bg-slate-800 p-4 ${className ?? ''}`}
    >
      <h2 className="font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
      {hint && <p className="mt-0.5 mb-3 text-xs text-slate-400">{hint}</p>}
      {children}
    </section>
  );
}

function Table({ head, rows }: { head: string[]; rows: React.ReactNode[][] }) {
  return (
    /* 宽表在自己的容器里横向滚,不让整页跟着滚 */
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-slate-500 border-b border-slate-200 dark:border-slate-700">
            {head.map((h, i) => (
              <th key={h} className={`py-2 font-medium ${i === 0 ? 'text-left' : 'text-right'}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 dark:border-slate-700/50 last:border-0">
              {r.map((cell, j) => (
                <td
                  key={j}
                  className={`py-2 ${
                    j === 0
                      ? 'text-left text-slate-900 dark:text-slate-100'
                      : 'text-right tabular-nums text-slate-600 dark:text-slate-300'
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty({ children, className }: { children: React.ReactNode; className?: string }) {
  return <p className={`text-sm text-slate-400 ${className ?? ''}`}>{children}</p>;
}
